import type { ViewerShell } from '../../shell';
import type { Viewport } from '../../viewport';
import type { AssistantFinding, Box, FindingStatus } from './api';
import { frameAt } from './context';

/**
 * Suggested findings drawn on their slice: a dashed box per finding that has one, coloured by its
 * review status, redrawn on every render of a viewport showing that image. Drawing only — nothing
 * here touches the annotation store (an accepted finding becomes an annotation only when the
 * radiologist draws it with the tools).
 */

export const STATUS_COLORS: Record<FindingStatus, string> = {
  pending: '#ffb300',
  accepted: '#3fb950',
  corrected: '#58a6ff',
  rejected: '#8b949e',
};

const DASH = [6, 4];
const LINE_WIDTH = 2;
const LABEL_FONT = '12px Arial, Helvetica, sans-serif';
const LABEL_GAP_PX = 4;
const LABEL_MAX = 40;

const findings = new Map<string, AssistantFinding>();
/** How many boxes the last render of each viewport drew (test hook). */
const drawnCount = new WeakMap<HTMLElement, number>();
let shellRef: ViewerShell | null = null;

/** Adds or replaces findings (by id) and redraws every viewport. */
export function setFindings(list: AssistantFinding[]): void {
  list.forEach((f) => findings.set(f.id, f));
  redraw();
}

export function finding(id: string): AssistantFinding | undefined {
  return findings.get(id);
}

export function allFindings(): AssistantFinding[] {
  return [...findings.values()];
}

/** Findings with a box on the image `viewport` shows. */
function findingsOn(viewport: Viewport): AssistantFinding[] {
  const stack = viewport.currentStack;
  const index = viewport.currentIndex();
  if (!stack || index === undefined) return [];
  const at = frameAt(stack.series, index);
  if (!at) return [];
  return [...findings.values()].filter((f) => {
    const loc = f.location;
    if (!loc?.box) return false;
    if (loc.instance_uid) return loc.instance_uid === at.instance.sopInstanceUID && loc.frame === at.frame;
    return loc.series_uid === stack.series.seriesInstanceUID && loc.index === index;
  });
}

function corners(box: Box, columns: number, rows: number): Array<{ x: number; y: number }> {
  const [x0, y0, x1, y1] = [box[0] * columns, box[1] * rows, box[2] * columns, box[3] * rows];
  return [{ x: x0, y: y0 }, { x: x1, y: y0 }, { x: x1, y: y1 }, { x: x0, y: y1 }];
}

function drawFinding(ctx: CanvasRenderingContext2D, element: HTMLElement, f: AssistantFinding, size: { columns: number; rows: number }): void {
  const box = f.location?.box;
  if (!box) return;
  // Through pixelToCanvas, so zoom, pan, rotation and flips are honoured.
  const points = corners(box, size.columns, size.rows).map((p) => window.cornerstone.pixelToCanvas(element, p));
  const color = STATUS_COLORS[f.status];
  ctx.strokeStyle = color;
  ctx.lineWidth = LINE_WIDTH;
  ctx.setLineDash(DASH);
  ctx.beginPath();
  points.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
  ctx.closePath();
  ctx.stroke();
  const top = points.reduce((a, b) => (b.y < a.y || (b.y === a.y && b.x < a.x) ? b : a));
  ctx.setLineDash([]);
  ctx.font = LABEL_FONT;
  ctx.fillStyle = color;
  ctx.shadowColor = '#000';
  ctx.shadowBlur = 2;
  const text = f.label.length > LABEL_MAX ? `${f.label.slice(0, LABEL_MAX - 1)}…` : f.label;
  ctx.fillText(text, top.x, Math.max(12, top.y - LABEL_GAP_PX));
}

function draw(viewport: Viewport): void {
  const enabled = window.cornerstone.getEnabledElement(viewport.element);
  const image = enabled.image;
  if (!image) return;
  const shown = findingsOn(viewport);
  drawnCount.set(viewport.element, shown.length);
  if (shown.length === 0) return;
  const ctx = enabled.canvas.getContext('2d');
  if (!ctx) return;
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  for (const f of shown) drawFinding(ctx, viewport.element, f, image);
  ctx.restore();
}

/** Re-renders every viewport with an image (the boxes are drawn on its render). */
export function redraw(): void {
  for (const vp of shellRef?.viewports() ?? []) {
    if (vp.currentImage()) window.cornerstone.updateImage(vp.element);
  }
}

/** Boxes drawn on the last render of viewport `cell` (test hook). */
export function drawnOn(cell: number): number {
  const vp = shellRef?.viewports()[cell];
  return vp ? drawnCount.get(vp.element) ?? 0 : 0;
}

export function install(shell: ViewerShell): void {
  shellRef = shell;
  shell.on('image-rendered', ({ viewport }) => draw(viewport));
}
